
import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, TooltipProps } from "recharts";

interface DonutChartProps {
  data: any[];
  category: string;
  index: string;
  colors?: string[];
  valueFormatter?: (value: number) => string;
  showLabel?: boolean;
  showLegend?: boolean;
  className?: string;
}

export function DonutChart({
  data,
  category,
  index,
  colors = ["hsl(var(--primary))", "#2563eb", "#16a34a", "#d97706", "#dc2626"],
  valueFormatter = (value: number) => value.toString(),
  showLabel = false,
  showLegend = false,
  className,
}: DonutChartProps) {
  // Custom tooltip component
  const CustomTooltip = (props: TooltipProps<any, any>) => {
    const { active, payload } = props;

    if (!active || !payload) return null;

    const data = payload[0]?.payload;
    
    return (
      <div className="bg-background border rounded-md shadow-md p-2 text-sm">
        <div className="font-medium">{data[index]}</div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: payload[0].color }}
          />
          <span className="text-muted-foreground">
            {valueFormatter(data[category])}
          </span>
        </div>
      </div>
    );
  };

  // CustomLabel component for showing value in the center
  const CustomLabel = ({ viewBox, value }: any) => {
    const { cx, cy } = viewBox;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-xl font-medium fill-foreground"
      >
        {value}
      </text>
    );
  };

  // Custom legend
  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center gap-4 text-xs pt-4">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={1}
            dataKey={category}
            nameKey={index}
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
            {showLabel && (
              <CustomLabel
                viewBox={{ cx: "50%", cy: "50%" }}
                value={data.reduce((sum, item) => sum + item[category], 0)}
              />
            )}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={<CustomLegend />} />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
